import React from 'react';
import { StyleProp, View, ViewStyle, Pressable } from 'react-native';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Controller, UseFormReturn } from 'react-hook-form';
import inputStylesheet from '../styles/inputStyles';

type MyCheckboxParameters<T> = {
    options: { label: string; value: T; }[];
    editable?: boolean;
    style?: StyleProp<ViewStyle>;
    labelFontSize?: number;
    label?: string;
    required?: boolean;
} & ({
    form?: undefined;
    fieldName?: undefined;
    selected: string | boolean | number | null;
    setSelected: (value: T) => void;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    selected?: undefined;
    setSelected?: undefined;
});

function RadioGroup<T>({
    options,
    selected,
    onSelected,
    editable,
    labelFontSize,
    labelHeight,
    style,
}: {
    options: MyCheckboxParameters<T>['options'];
    selected: unknown;
    onSelected: (value: T) => void;
    editable: boolean;
    labelFontSize?: number;
    labelHeight: number;
    style?: StyleProp<ViewStyle>;
}) {
    const { styles, theme } = useStyles(stylesheet);

    return (
        <View style={!style ? styles.radioContainer : style}>
            {options.map((obj, i) => (
                <Pressable
                    key={i}
                    style={styles.option}
                    onPress={() => editable && onSelected(obj.value)}
                    disabled={!editable}
                >
                    <View style={styles.outerCircle({ borderColor: selected === obj.value ? theme.colors.primary[500] : theme.colors.neutral[300] })}>
                        {selected === obj.value && (
                            <View style={styles.innerCircle} />
                        )}
                    </View>
                    <MyAppText style={styles.label(labelFontSize ?? 16, labelHeight)}>
                        {obj.label}
                    </MyAppText>
                </Pressable>
            ))}
        </View>
    );
}

export default function MyRadio<T>({ label, required, options, selected, setSelected, style, labelFontSize, editable = true, form, fieldName }: MyCheckboxParameters<T>) {
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    if (form && fieldName) {
        return (
            <Controller
                control={form.control}
                name={fieldName}
                render={({ field }) => (
                    <View style={[defaultInputStyle.container]}>
                        {label && <View style={defaultInputStyle.labelContainer}>
                            {label && <MyAppText style={defaultInputStyle.label}>{label}</MyAppText>}
                            {required && label ?
                                <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                                : null}
                        </View>
                        }
                        <RadioGroup
                            options={options}
                            selected={field.value}
                            onSelected={(value) => editable && field.onChange(value)}
                            editable={editable}
                            labelFontSize={labelFontSize}
                            labelHeight={defaultInputStyle.input({}).height ?? 25}
                            style={style}
                        />
                    </View>
                )}
            />
        );
    } else if (selected !== undefined && setSelected !== undefined) {
        return (
            <View style={[defaultInputStyle.container]}>
                {label && <View style={defaultInputStyle.labelContainer}>
                    {label && <MyAppText style={defaultInputStyle.label}>{label}</MyAppText>}
                    {required && label ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                </View>
                }
                <RadioGroup
                    options={options}
                    selected={selected}
                    onSelected={setSelected}
                    editable={editable}
                    labelFontSize={labelFontSize}
                    labelHeight={defaultInputStyle.input({}).height ?? 25}
                    style={style}
                />
            </View>
        );
    }
}

const stylesheet = createStyleSheet(theme => ({
    radioContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        flex: 1,
    },
    option: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
    },
    outerCircle: ({ borderColor }: { borderColor: string; }) => ({
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor,
        alignItems: 'center',
        justifyContent: 'center'
    }),
    innerCircle: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.primary[500],
    },
    label: (fontSize: number, labelHeight: number) => ({
        color: theme.colors.neutral[400],
        fontSize,
        height: labelHeight,
        fontWeight: '400',
        alignContent: 'center',
    }),
}));
